import {Button, Empty, Flex, Space, Tabs, Tag} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAngleLeft} from "@fortawesome/free-solid-svg-icons";
import Title from "antd/es/typography/Title";
import React, {useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {SettingContent} from "./components/setting/SettingContent";
import {useAppDispatch, useAppSelector} from "../../../../feature/tools";
import {fetchDraft, saveDraft} from "../../../../feature/project/draftSlice";
import {Draft} from "../../../../feature/Draft";
import {PlotContent} from "./components/plot/PlotContent";
import {openProjectSettings} from "../../../../feature/project/appSettingsSlice";
import {editorRoles, NavigateWithUserPermission} from "../../../common/WithUserPermission";

export const StoryCrafterContent = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const {projectId, storyId, draftTab} = useParams()
    const creator = useAppSelector(state => state.users.creator)
    const draft = useAppSelector(state => state.draftSlice.draft)
    const saving = useAppSelector(state => state.draftSlice.saving)
    const saved = useAppSelector(state => state.draftSlice.saved)
    const [lastDraft, setLastDraft] = React.useState<Draft | null>(draft)
    const storageProvider = useAppSelector(state => state.storageSlice.storageProvider)
    const projectStorageProvider = useAppSelector(state => state.storageSlice.projectStorageProviderSettings)

    const tabs = [
        {
            label: "Plot",
            key: "plot",
            children: <PlotContent/>,
        },
        {
            label: "Setting & Lore",
            key: "setting",
            children: <SettingContent/>,
        },
    ]

    useEffect(() => {
        if (storyId && storageProvider && projectId) {
            dispatch(fetchDraft({
                storageProvider: storageProvider,
                path: creator?.id + "/" + projectId + "/" + storyId + "/",
            }))
        }
    }, [storyId, storageProvider]);

    useEffect(() => {
        if (!lastDraft) {
            setLastDraft(draft)
        }
    }, [draft]);

    return (
        <NavigateWithUserPermission roles={editorRoles}>
            <Flex gap={10} vertical={true} style={{width: "100%", height: "100%"}} className={'crafting'}>
                <Flex justify={'space-between'}>

                    <Space>
                        <Button type={'text'}
                                onClick={() => navigate(-1)}
                                icon={<FontAwesomeIcon icon={faAngleLeft}/>}/>
                        <Title level={4} style={{margin: 0}}>Story Crafter</Title>
                    </Space>
                    {storageProvider != undefined ?
                        <Space>

                            {saved ? new Date().getDate() == saved.getDate() ? `Today at ${saved.toLocaleString(undefined, {hour12: false}).slice(11, 17)}` : saved.toLocaleString(undefined, {hour12: false}).slice(0, 17).replace(", ", " ") : "Never saved"}
                            <Button disabled={JSON.stringify(lastDraft) == JSON.stringify(draft)} loading={saving}
                                    type={'primary'} onClick={() => {
                                if (storyId) {
                                    setLastDraft(draft)
                                    dispatch(saveDraft({
                                        storageProvider: storageProvider,
                                        draft: draft,
                                        path: creator?.id + "/" + projectId + "/" + storyId + "/",
                                    }))
                                }
                            }}>Save</Button>
                        </Space> : ''}
                </Flex>
                {projectStorageProvider && !storageProvider ?
                    <Empty description={<>Please select a Storage Provider first in the <Button
                        size={'small'}
                        onClick={() => dispatch(openProjectSettings('storage'))}
                    >Project Settings
                    </Button></>}/> : <>
                        <Flex vertical={true} gap={10} style={{flexGrow: 1, overflow: 'hidden'}}>
                            <Tabs
                                className="draft-tabs"
                                style={{height: "100%", overflow: 'clip'}}
                                items={tabs}
                                activeKey={draftTab ?? 'plot'}
                                onChange={(key) => {
                                    navigate(`/projects/${projectId}/stories/${storyId}/story-crafter/${key}`)
                                }}
                                tabBarExtraContent={{
                                    right: <Flex gap={10} style={{padding: 10}}>
                                        <b>Statistics</b>
                                        <Tag>Total
                                            chapters: <b>{draft.story.acts?.flatMap((act) => act.chapters)?.length ?? 0}</b></Tag>
                                        <Tag>Total
                                            scenes: <b>{draft.story.acts?.flatMap((act) => act.chapters?.flatMap((ch) => ch.sections?.flatMap(s => s.scenes)))?.length ?? 0}</b></Tag>
                                        <Tag>Total
                                            shots: <b>{draft.story.acts?.flatMap((act) => act.chapters?.flatMap((ch) => ch.sections?.flatMap(s => s.scenes)))?.length ?? 0}</b></Tag>
                                        <Tag>Total
                                            words: <b>{draft.story.acts?.flatMap(act => act.chapters ?? []).flatMap(chapter => chapter.sections ?? []).flatMap(s => s.scenes ?? []).flatMap(scene => scene.shots ?? []).reduce((acc, item) => acc + (item.narration?.text || '').trim().replaceAll('  ', '').split(' ').length + (item.description || '').trim().replaceAll('  ', '').split(' ').length, 0)}</b></Tag>
                                        <Tag>Total narration
                                            words: <b>{draft.story.acts?.flatMap(act => act.chapters ?? []).flatMap(chapter => chapter.sections ?? []).flatMap(s => s.scenes ?? []).flatMap(scene => scene.shots ?? []).reduce((acc, item) => acc + (item.narration?.text || '').trim().replaceAll('  ', '').split(' ').length, 0)}</b></Tag>
                                        <Tag>Reading
                                            time: <b>{Math.round(draft.story.acts?.flatMap(act => act.chapters ?? []).flatMap(chapter => chapter.sections ?? []).flatMap(s => s.scenes ?? []).flatMap(scene => scene.shots ?? []).reduce((acc, item) => acc + (item.narration?.text || '').trim().replaceAll('  ', '').split(' ').length, 0) / 130)} minutes</b></Tag>
                                    </Flex>
                                }}
                            />
                        </Flex>
                    </>}
            </Flex>
        </NavigateWithUserPermission>
    );
}